/* eslint-disable react/prop-types */
import React from 'react';
import { StyleSheet, View } from 'react-native';
import Colors from '@global/colors';
import { currency } from '@global/helpers';
import { isWeb } from '@global/screens';
import SociollaText from '@components/sociolla/SociollaText';
import Image from '@components/global/Image';
import { resolvePromoStatus } from '@/screens/sales-activity/components/todo-list/on-going/todo-visibility-update/TodoVisibilityUpdate.utils';

const PROMO_STATUS_LABEL = {
  no_promotion: 'No Promotion',
  not_implemented: 'Not Implemented',
  implemented: 'Implemented',
};

const VisibilityUpdateProductCard = ({
  item = {},
  userRole = '',
  isValueSellOutOnly = false,
}) => {
  const getImage = (data) => {
    let result = '';
    if (data?.images?.length && data.images[0].url) {
      result = data.images[0].url;
    }
    if (data?.image) {
      result = data.image;
    }
    return result;
  };

  const getReference = (data) => {
    let result = '-';

    if (data?.combinations?.length && data?.combinations[0]?.reference) {
      result = data.combinations[0].reference;
    }
    if (data?.combination?.reference) {
      result = data.combination.reference;
    }

    return result;
  };

  const renderFieldRow = (
    field,
    index,
    totalFields,
    { compact = false } = {},
  ) => (
    <View
      key={index}
      style={[
        styles.fieldRow,
        index === totalFields - 1 && styles.fieldRowLast,
      ]}>
      <View style={styles.fieldLabelWrapper}>
        {field.labelComponent ? (
          field.labelComponent
        ) : (
          <SociollaText
            style={[styles.fieldLabel, compact && styles.fieldLabelSmall]}>
            {field.label}:
          </SociollaText>
        )}
      </View>
      <SociollaText
        type="medium"
        style={[styles.fieldValue, compact && styles.fieldValueSmall]}>
        {field.value}
      </SociollaText>
    </View>
  );

  const renderPrevStockFields = () => (
    <View style={styles.fieldList}>
      {[
        {
          label: 'Pricing',
          value: item?.pricing ? currency(item?.pricing, true) : 0,
        },
        {
          label: 'Expires in 3 months',
          value: item.expires_in_3_months || 0,
        },
        {
          label: 'Expires in 12 months',
          value: item.expires_in_12_months || 0,
        },
      ].map((field, index, arr) =>
        renderFieldRow(field, index, arr.length, { compact: true }),
      )}
    </View>
  );

  const renderNonPrevStockFields = () => {
    const originalEndingStock =
      item?.result_quantity ||
      item?.quantity + item?.sell_in - item?.sell_out - item?.return ||
      0;
    const endingStock = item?.is_ending_edited
      ? item?.edited_result_quantity
      : originalEndingStock;

    const fields = [
      { label: 'Previous stock', value: item?.quantity || 0 },
      {
        label: 'Pricing',
        value: item?.pricing ? currency(item?.pricing, true) : 0,
      },
      { label: 'Sell in', value: item?.sell_in || 0 },
      { label: 'Sell out', value: item?.sell_out || 0 },
      {
        label: 'Value sell out',
        value: currency(
          item?.value_sell_out || item?.sell_out * item?.price || 0,
          true,
        ),
      },
      { label: 'Return', value: item?.return || 0 },
      {
        label: 'Expires in 3 months',
        value: item?.expires_in_3_months || 0,
      },
      {
        label: 'Expires in 12 months',
        value: item?.expires_in_12_months || 0,
      },
      { label: 'Ending Stock', value: endingStock },
    ];

    return (
      <View style={styles.fieldList}>
        {fields.map((field, index, arr) =>
          renderFieldRow(field, index, arr.length),
        )}
        {item?.is_ending_edited ? (
          <View style={styles.auditTrailCard}>
            <SociollaText style={styles.auditTrailText}>
              {'Ending Stock Edited: '}
              <SociollaText type="semibold" style={styles.auditTrailText}>
                {originalEndingStock}
              </SociollaText>
              {' → '}
              <SociollaText type="semibold" style={styles.auditTrailText}>
                {item?.edited_result_quantity}
              </SociollaText>
            </SociollaText>
            <SociollaText style={styles.auditTrailText}>
              {'Reason: '}
              <SociollaText type="semibold" style={styles.auditTrailText}>
                {item?.edited_reason_result_quantity || '-'}
              </SociollaText>
            </SociollaText>
          </View>
        ) : null}
      </View>
    );
  };

  const renderPromoterFields = () => {
    const originalEndingStock =
      item?.result_quantity ||
      (item?.quantity || 0) + (item?.sell_in || 0) - (item?.sell_out || 0) ||
      0;
    const endingStock = item?.is_ending_edited
      ? item?.edited_result_quantity
      : originalEndingStock;

    const valueSellOutField = {
      label: 'Value sell out',
      value: currency(item?.value_sell_out || 0, true),
    };
    // B2B-5929: online activity only shares value sell out
    const fields = isValueSellOutOnly
      ? [valueSellOutField]
      : [
          { label: 'Beginning stock', value: item?.quantity || 0 },
          { label: 'Sell in', value: item?.sell_in || 0 },
          { label: 'Sell out', value: item?.sell_out || 0 },
          valueSellOutField,
          { label: 'Ending stock', value: endingStock },
        ];

    return (
      <View style={styles.fieldList}>
        {fields.map((field, index, arr) =>
          renderFieldRow(field, index, arr.length),
        )}
        {item?.is_ending_edited ? (
          <View style={styles.auditTrailCard}>
            <SociollaText style={styles.auditTrailText}>
              {'Ending Stock Edited: '}
              <SociollaText type="semibold" style={styles.auditTrailText}>
                {originalEndingStock}
              </SociollaText>
              {' → '}
              <SociollaText type="semibold" style={styles.auditTrailText}>
                {item?.edited_result_quantity}
              </SociollaText>
            </SociollaText>
            <SociollaText style={styles.auditTrailText}>
              {'Reason: '}
              <SociollaText type="semibold" style={styles.auditTrailText}>
                {item?.edited_reason_result_quantity || '-'}
              </SociollaText>
            </SociollaText>
          </View>
        ) : null}
      </View>
    );
  };

  const renderMerchandiserResultFields = () => {
    const promoList = item?.promo_list || [];
    const promoStatus = resolvePromoStatus(item);
    const fields = [
      {
        label: 'Promo Status',
        value: PROMO_STATUS_LABEL[promoStatus] || '-',
      },
    ];
    if (promoStatus === 'implemented' && promoList.length > 0) {
      fields.push({
        label: 'Promotions',
        value: promoList.map((p) => p.name || '-').join(', '),
      });
    }
    fields.push(
      {
        label: 'Wobbler',
        value:
          item?.is_wobbler === true
            ? 'Yes'
            : item?.is_wobbler === false
            ? 'No'
            : '-',
      },
      {
        label: 'Price',
        value: item?.pricing ? currency(item.pricing, true) : '-',
      },
      { label: 'Notes', value: item?.notes || '-' },
    );
    return (
      <View style={styles.fieldList}>
        {fields.map((field, index, arr) =>
          renderFieldRow(field, index, arr.length),
        )}
      </View>
    );
  };

  const renderDetailItem = () => {
    if (userRole == 'beauty_advisor') {
      return item.is_beginning
        ? renderPrevStockFields()
        : renderNonPrevStockFields();
    }
    if (userRole == 'promotor') {
      return renderPromoterFields();
    }
    if (userRole == 'merchandiser') {
      return renderMerchandiserResultFields();
    }
    return false;
  };

  return (
    <View testID="todo-visibility-update-product-card" style={styles.card}>
      <View style={styles.topSection}>
        <View style={styles.productImageWrapper}>
          <Image
            style={styles.productImage}
            width={50}
            height={50}
            source={getImage(item)}
          />
        </View>
        <View style={styles.productInfoWrapper}>
          <SociollaText style={styles.brandText}>
            {item.brand && item.brand.name ? item.brand.name : '-'}
          </SociollaText>
          <SociollaText type="medium" style={styles.productName}>
            {item.name || '-'}
          </SociollaText>
          <SociollaText style={styles.referenceText}>
            {getReference(item)}
          </SociollaText>
        </View>
      </View>

      <View style={styles.bottomSection}>
        {((userRole == 'beauty_advisor' && item.is_beginning) ||
          (userRole != 'beauty_advisor' && userRole != 'promotor')) && (
          <View style={styles.fieldList}>
            {renderFieldRow(
              { label: 'Quantity', value: item.quantity || 0 },
              0,
              1,
              { compact: true },
            )}
          </View>
        )}
        {renderDetailItem()}
        {userRole == 'salesman' ? (
          <View style={styles.fieldList}>
            {renderFieldRow(
              {
                label: 'Previous Stocks',
                value: item.previous_stock || 0,
              },
              0,
              1,
              { compact: true },
            )}
          </View>
        ) : null}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  card: {
    width: '100%',
    maxWidth: isWeb ? 952 : '100%',
    alignSelf: 'center',
    padding: 14,
    marginBottom: isWeb ? 12 : 8,
    backgroundColor: Colors.white,
    borderColor: Colors.grey0,
    borderWidth: 1,
    borderRadius: isWeb ? 10 : 0,
  },
  topSection: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  productImageWrapper: {
    width: 54,
    height: 54,
    borderRadius: 8,
    overflow: 'hidden',
    marginRight: 12,
    backgroundColor: Colors.grey3,
  },
  productImage: {
    width: 54,
    height: 54,
  },
  productInfoWrapper: {
    flex: 1,
  },
  brandText: {
    fontSize: 12,
    color: Colors.grey1,
    marginBottom: 4,
  },
  productName: {
    fontSize: 14,
    lineHeight: 21,
    color: Colors.grey2,
  },
  referenceText: {
    fontSize: 12,
    color: Colors.grey1,
    marginTop: 4,
  },
  bottomSection: {
    marginTop: isWeb ? 12 : 14,
    paddingTop: isWeb ? 10 : 12,
    borderTopColor: Colors.grey0,
    borderTopWidth: 1,
  },
  fieldList: {
    width: '100%',
  },
  fieldRow: {
    width: '100%',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    paddingVertical: isWeb ? 5 : 7,
  },
  fieldRowLast: {
    paddingBottom: 2,
  },
  fieldLabelWrapper: {
    flex: 1,
    paddingRight: 12,
  },
  fieldLabel: {
    fontSize: isWeb ? 13 : 14,
    color: Colors.grey1,
    lineHeight: isWeb ? 19 : 21,
  },
  fieldLabelSmall: {
    fontSize: 12,
    lineHeight: isWeb ? 17 : 18,
  },
  fieldValue: {
    flex: 1,
    fontSize: isWeb ? 13 : 14,
    color: Colors.blue1,
    textAlign: 'right',
    lineHeight: isWeb ? 19 : 21,
  },
  fieldValueSmall: {
    fontSize: 12,
    lineHeight: isWeb ? 17 : 18,
  },
  auditTrailCard: {
    marginTop: 8,
    paddingVertical: 8,
    paddingHorizontal: 10,
    backgroundColor: Colors.blue12,
    borderLeftWidth: 3,
    borderLeftColor: Colors.blue0,
    borderRadius: 4,
  },
  auditTrailText: {
    fontSize: 12,
    color: Colors.grey2,
    lineHeight: 18,
  },
});

export default React.memo(VisibilityUpdateProductCard);
